import { useParams } from 'react-router-dom';
import { useMemo } from 'react';

import { PromocodeFragment } from '@occc/graphql';
import { DateTimeFormat, formatDateTime, parseDate } from '@occc-app/lib/date';
import { useGetCampaignsOffer } from '@occc-app/dataAccessLayer/campaign/queries';
import {
  LoadingOverlay,
  NO_DATA_PLACEHOLDER,
  NotFoundPage,
  Page,
  useAppLayoutContextConsumer,
} from '@occc-app/design-system';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';

import { PromocodeDetailsPage } from './promocode-details.page';
import { getBreadcrumbs } from './promocode-details.constants';

import { messages } from '../../promocodes.messages';

export const PromocodeDetailsController = () => {
  const {
    id: campaignId = '',
    promocodesId = '',
    promocodeId,
  } = useParams<{
    id: string;
    promocodesId: string;
    promocodeId: string;
  }>();

  const {
    data: { campaign, offer },
    loading,
  } = useGetCampaignsOffer(campaignId, promocodesId);

  const breadcrumbs = useMemo(
    () =>
      getBreadcrumbs({
        campaignId: campaign?.uuid ?? '',
        campaignName: campaign?.name ?? '',
        campaignComponentId: offer?.id ?? '',
        campaignComponentName: offer?.name ?? '',
      }),
    [campaign?.name, campaign?.uuid, offer?.id, offer?.name],
  );
  useAppLayoutContextConsumer(breadcrumbs);

  const promocode = useMemo<PromocodeFragment | null>(() => {
    if (!offer?.earningMechanism) return null;
    if (!earningMechanismHelper.isPromocodes(offer.earningMechanism)) return null;
    return (
      offer.earningMechanism.promocodes.find((promocode) => promocode.id === promocodeId) || null
    );
  }, [offer, promocodeId]);

  if (!promocode && loading) return <LoadingOverlay />;
  if (!promocode) return <NotFoundPage />;

  const createdByName = promocode.createdBy.name || promocode.createdBy.email;
  const startDateString = formatDateTime(
    parseDate(promocode.startDate),
    DateTimeFormat.DateTime12h,
  );
  const endDateString = promocode.endDate
    ? formatDateTime(parseDate(promocode.endDate), DateTimeFormat.DateTime12h)
    : NO_DATA_PLACEHOLDER;

  return (
    <Page
      pageHeaderProps={{
        title: messages.promocodeDetailsPageTitle,
      }}
      browserTabTitle={messages.promocodeDetailsPageTitle}
      containerProps={{ variant: 'md' }}
    >
      <PromocodeDetailsPage
        code={promocode.code}
        description={promocode.description || NO_DATA_PLACEHOLDER}
        createdBy={createdByName}
        startDate={startDateString}
        endDate={endDateString}
        stats={promocode.stats}
        status={promocode.status}
        publicationStatus={promocode.publicationStatus}
      />
    </Page>
  );
};
